/**
 * Self-managed authentication client service
 * Handles authentication against the authentication-v2 backend service
 */

// Direct import to avoid circular dependency through barrel export
// (providers/index.ts → brandConfigProvider → featureToggleProvider → services/index.ts → here)
import { initializeMixpanel, MixpanelClient } from '../providers/mixpanelProvider/mixpanelProvider';
import { UnauthorizedException } from '@40seas/errors';
import { z } from 'zod';

// Zod schemas for runtime validation at system boundary
export const AuthTokensSchema = z.object({
    accessToken: z.string().min(1, 'accessToken is required'),
    refreshToken: z.string().min(1, 'refreshToken is required'),
    expiresIn: z.number().positive('expiresIn must be positive'),
});

export const AuthUserSchema = z.object({
    id: z.string().min(1, 'id is required'),
    email: z.string().email('invalid email format'),
    name: z.string().nullish(),
    picture: z.string().url().or(z.literal('')).nullish(),
    roles: z.array(z.string()).nullish(),
});

// Derive TypeScript types from schemas
export type AuthTokens = z.infer<typeof AuthTokensSchema>;
export type AuthUser = z.infer<typeof AuthUserSchema>;

export interface LoginResponse {
    user: AuthUser;
    tokens: AuthTokens;
}

export interface RegistrationResponse {
    message: string;
    email: string;
    requiresVerification: boolean;
}

const ACCESS_TOKEN_KEY = 'self_managed_access_token';
const REFRESH_TOKEN_KEY = 'self_managed_refresh_token';
const USER_KEY = 'self_managed_user';
const TOKEN_EXPIRES_AT_KEY = 'self_managed_token_expires_at';
const FORCE_PASSWORD_RESET_KEY = 'self_managed_password_reset_redirect_required';

class SelfManagedAuthServiceClass {
    private baseUrl: string;
    private mixpanel: MixpanelClient;
    // Tracks in-flight refresh request to prevent concurrent refresh token calls
    // This prevents race conditions when multiple components call getAccessTokenSilently()
    // concurrently while the token is expired
    private refreshPromise: Promise<AuthTokens> | null = null;

    constructor() {
        this.baseUrl = process.env.AUTH_V2_URL || 'http://localhost:3334';
        this.mixpanel = initializeMixpanel();
    }

    /**
     * Safely extract email domain for analytics tracking.
     * Handles edge cases like missing @, multiple @, or empty strings.
     */
    private getEmailDomain(email: string): string {
        if (!email || typeof email !== 'string') {
            return 'unknown';
        }
        const atIndex = email.lastIndexOf('@');
        if (atIndex === -1 || atIndex === email.length - 1) {
            return 'unknown';
        }
        const domain = email.slice(atIndex + 1);
        // Basic validation: domain should have at least one dot and no spaces
        if (!domain.includes('.') || domain.includes(' ')) {
            return 'invalid';
        }
        return domain;
    }

    /**
     * Login with email and password
     */
    async loginWithPassword(email: string, password: string): Promise<LoginResponse> {
        localStorage.removeItem(FORCE_PASSWORD_RESET_KEY);

        // Track login attempt
        this.mixpanel.track('Auth: Login Attempt', {
            provider: 'local',
            email_domain: this.getEmailDomain(email),
        });

        const response = await fetch(`${this.baseUrl}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            let errorMessage = 'Login failed';
            try {
                const error = await response.json();
                errorMessage = error.message || errorMessage;
            } catch {
                errorMessage = `Login failed: ${response.status} ${response.statusText}`;
            }

            // Track login failure
            this.mixpanel.track('Auth: Login Failed', {
                provider: 'local',
                email_domain: this.getEmailDomain(email),
                error: errorMessage,
                status_code: response.status,
            });

            if (response.status === 418) {
                localStorage.setItem(FORCE_PASSWORD_RESET_KEY, 'true');
            }

            throw new Error(errorMessage);
        }

        localStorage.removeItem(FORCE_PASSWORD_RESET_KEY);

        const data = await response.json();
        // Backend returns flat structure: { accessToken, refreshToken, expiresIn, user }
        const tokens = this.extractTokens(data);
        const user = this.validateUser(data.user);
        // Store user before tokens to ensure user data is available when auth-tokens-changed event fires
        this.storeUser(user);
        this.storeTokens(tokens);

        // Track login success
        this.mixpanel.track('Auth: Login Success', {
            provider: 'local',
            email_domain: this.getEmailDomain(email),
            user_id: user.id,
        });

        // Set user properties
        this.mixpanel.identify(user.id);
        this.mixpanel.people.set({
            $email: email,
            auth_provider: 'local',
            last_login: new Date().toISOString(),
        });

        return { user, tokens };
    }

    /**
     * Register a new user with email and password
     * Note: Registration now requires email verification before login
     */
    async register(email: string, password: string, name?: string): Promise<RegistrationResponse> {
        // Track registration attempt
        this.mixpanel.track('Auth: Registration Attempt', {
            provider: 'local',
            email_domain: this.getEmailDomain(email),
        });

        const response = await fetch(`${this.baseUrl}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, name }),
        });

        if (!response.ok) {
            let errorMessage = 'Registration failed';
            try {
                const error = await response.json();
                errorMessage = error.message || errorMessage;
            } catch {
                errorMessage = `Registration failed: ${response.status} ${response.statusText}`;
            }

            // Track registration failure
            this.mixpanel.track('Auth: Registration Failed', {
                provider: 'local',
                email_domain: this.getEmailDomain(email),
                error: errorMessage,
                status_code: response.status,
            });

            throw new Error(errorMessage);
        }

        const data = await response.json();

        // Track registration success
        this.mixpanel.track('Auth: Registration Success', {
            provider: 'local',
            email_domain: this.getEmailDomain(email),
            email: data.email,
        });

        // Backend returns: { message, email } - requires email verification before login
        // Do NOT attempt to extract tokens or store user data
        return {
            message: data.message || 'Registration successful! Please check your email to verify your account.',
            email: data.email,
            requiresVerification: true,
        };
    }

    /**
     * Get Google OAuth authorization URL
     */
    async getGoogleAuthUrl(returnTo?: string): Promise<string> {
        // Track OAuth initiation
        this.mixpanel.track('Auth: OAuth Initiated', {
            provider: 'google',
        });

        try {
            const params = new URLSearchParams();
            if (returnTo) {
                params.set('returnTo', returnTo);
            }
            const queryString = params.toString();
            const url = `${this.baseUrl}/auth/google${queryString ? `?${queryString}` : ''}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to get Google auth URL');
            }
            const data = await response.json();
            return data.authUrl;
        } catch (error) {
            this.mixpanel.track('Auth: OAuth Failed', {
                provider: 'google',
                error: error instanceof Error ? error.message : 'Unknown error',
                stage: 'init',
            });
            throw error;
        }
    }

    /**
     * Handle Google OAuth callback
     */
    async handleGoogleCallback(code: string, state?: string): Promise<LoginResponse> {
        const response = await fetch(`${this.baseUrl}/auth/google/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
            let errorMessage = 'Google authentication failed';
            try {
                const error = await response.json();
                errorMessage = error.message || errorMessage;
            } catch {
                errorMessage = `Google authentication failed: ${response.status} ${response.statusText}`;
            }

            // Track OAuth failure
            this.mixpanel.track('Auth: OAuth Failed', {
                provider: 'google',
                error: errorMessage,
                status_code: response.status,
                stage: 'callback',
            });

            throw new Error(errorMessage);
        }

        const data = await response.json();
        // Backend returns flat structure: { accessToken, refreshToken, expiresIn, user }
        const tokens = this.extractTokens(data);
        const user = this.validateUser(data.user);
        // Store user before tokens to ensure user data is available when auth-tokens-changed event fires
        this.storeUser(user);
        this.storeTokens(tokens);

        // Determine if this is a new user based on account creation time
        const isNewUser = data.isNewUser || false;

        // Track OAuth success
        this.mixpanel.track('Auth: OAuth Success', {
            provider: 'google',
            email_domain: this.getEmailDomain(user.email),
            user_id: user.id,
            is_new_user: isNewUser,
        });

        // Also track as login success for consistency
        this.mixpanel.track('Auth: Login Success', {
            provider: 'google',
            email_domain: this.getEmailDomain(user.email),
            user_id: user.id,
            is_new_user: isNewUser,
        });

        // Set user properties
        this.mixpanel.identify(user.id);
        this.mixpanel.people.set({
            $email: user.email,
            auth_provider: 'google',
            last_login: new Date().toISOString(),
        });

        return { user, tokens };
    }

    /**
     * Get Microsoft OAuth authorization URL
     */
    async getMicrosoftAuthUrl(returnTo?: string): Promise<string> {
        // Track OAuth initiation
        this.mixpanel.track('Auth: OAuth Initiated', {
            provider: 'microsoft',
        });

        try {
            const params = new URLSearchParams();
            if (returnTo) {
                params.set('returnTo', returnTo);
            }
            const queryString = params.toString();
            const url = `${this.baseUrl}/auth/microsoft${queryString ? `?${queryString}` : ''}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Failed to get Microsoft auth URL');
            }
            const data = await response.json();
            return data.authUrl;
        } catch (error) {
            this.mixpanel.track('Auth: OAuth Failed', {
                provider: 'microsoft',
                error: error instanceof Error ? error.message : 'Unknown error',
                stage: 'init',
            });
            throw error;
        }
    }

    /**
     * Handle Microsoft OAuth callback
     */
    async handleMicrosoftCallback(code: string, state?: string): Promise<LoginResponse> {
        const response = await fetch(`${this.baseUrl}/auth/microsoft/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ code, state }),
        });

        if (!response.ok) {
            let errorMessage = 'Microsoft authentication failed';
            try {
                const error = await response.json();
                errorMessage = error.message || errorMessage;
            } catch {
                errorMessage = `Microsoft authentication failed: ${response.status} ${response.statusText}`;
            }

            // Track OAuth failure
            this.mixpanel.track('Auth: OAuth Failed', {
                provider: 'microsoft',
                error: errorMessage,
                status_code: response.status,
                stage: 'callback',
            });

            throw new Error(errorMessage);
        }

        const data = await response.json();
        // Backend returns flat structure: { accessToken, refreshToken, expiresIn, user }
        const tokens = this.extractTokens(data);
        const user = this.validateUser(data.user);
        // Store user before tokens to ensure user data is available when auth-tokens-changed event fires
        this.storeUser(user);
        this.storeTokens(tokens);

        // Determine if this is a new user based on account creation time
        const isNewUser = data.isNewUser || false;

        // Track OAuth success
        this.mixpanel.track('Auth: OAuth Success', {
            provider: 'microsoft',
            email_domain: this.getEmailDomain(user.email),
            user_id: user.id,
            is_new_user: isNewUser,
        });

        // Also track as login success for consistency
        this.mixpanel.track('Auth: Login Success', {
            provider: 'microsoft',
            email_domain: this.getEmailDomain(user.email),
            user_id: user.id,
            is_new_user: isNewUser,
        });

        // Set user properties
        this.mixpanel.identify(user.id);
        this.mixpanel.people.set({
            $email: user.email,
            auth_provider: 'microsoft',
            last_login: new Date().toISOString(),
        });

        return { user, tokens };
    }

    /**
     * Resend email verification
     */
    async resendVerification(email: string): Promise<{ message: string }> {
        const response = await fetch(`${this.baseUrl}/auth/resend-verification`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
        });

        if (!response.ok) {
            let errorMessage = 'Failed to resend verification email';
            try {
                const error = await response.json();
                errorMessage = error.message || errorMessage;
            } catch {
                errorMessage = `Failed to resend verification email: ${response.status} ${response.statusText}`;
            }
            throw new Error(errorMessage);
        }

        return await response.json();
    }

    /**
     * Refresh the access token using the refresh token.
     * Uses request deduplication to prevent race conditions when multiple callers
     * request a refresh concurrently - all concurrent calls share the same promise.
     */
    async refreshToken(): Promise<AuthTokens> {
        // If a refresh is already in progress, return the existing promise
        // This prevents race conditions with refresh token rotation
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        const refreshToken = this.getRefreshToken();
        if (!refreshToken) {
            throw new Error('No refresh token available');
        }

        // Create and store the promise before starting the async operation
        this.refreshPromise = this.executeRefresh(refreshToken);

        try {
            return await this.refreshPromise;
        } finally {
            // Clear the promise so future calls can refresh again
            this.refreshPromise = null;
        }
    }

    /**
     * Internal method to execute the actual refresh token API call.
     * Separated from refreshToken() to keep the deduplication logic clean.
     */
    private async executeRefresh(refreshToken: string): Promise<AuthTokens> {
        const response = await fetch(`${this.baseUrl}/auth/refresh`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
            let errorMessage = 'Token refresh failed';
            try {
                const error = await response.json();
                errorMessage = error.message || errorMessage;
            } catch {
                errorMessage = `Token refresh failed: ${response.status} ${response.statusText}`;
            }
            this.clearTokens();
            throw new UnauthorizedException({
                message: errorMessage,
                code: 'TOKEN_REFRESH_FAILED',
            });
        }

        const data = await response.json();
        // Backend returns flat structure: { accessToken, refreshToken, expiresIn }
        const tokens = this.extractTokens(data);
        this.storeTokens(tokens);
        return tokens;
    }

    /**
     * Logout and revoke tokens
     */
    async logout(): Promise<void> {
        const refreshToken = this.getRefreshToken();
        if (refreshToken) {
            try {
                await fetch(`${this.baseUrl}/auth/logout`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken }),
                });
            } catch (error) {
                // Log logout errors for debugging but don't block the logout flow
                // The user will be logged out locally regardless of API response
                console.warn('Failed to revoke refresh token on logout:', error);
            }
        }
        this.clearTokens();
    }

    /**
     * Check if user is authenticated (token exists and is not expired)
     */
    isAuthenticated(): boolean {
        return !!this.getAccessToken() && !this.isTokenExpired();
    }

    /**
     * Check if the access token has expired
     */
    isTokenExpired(): boolean {
        const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);
        if (!expiresAt) return true;
        return Date.now() >= parseInt(expiresAt, 10);
    }

    /**
     * Get the current access token
     */
    getAccessToken(): string | null {
        return localStorage.getItem(ACCESS_TOKEN_KEY);
    }

    /**
     * Get the current refresh token
     */
    getRefreshToken(): string | null {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }

    /**
     * Get the current user
     */
    getUser(): AuthUser | null {
        const userStr = localStorage.getItem(USER_KEY);
        if (!userStr) return null;
        try {
            return AuthUserSchema.parse(JSON.parse(userStr));
        } catch {
            return null;
        }
    }

    /**
     * Extract and validate tokens from backend response using Zod schema
     * Backend returns: { accessToken, refreshToken, expiresIn, user, ... }
     * @throws ZodError if required token fields are missing or have wrong types
     */
    private extractTokens(data: Record<string, unknown>): AuthTokens {
        return AuthTokensSchema.parse(data);
    }

    /**
     * Validate user data from backend response using Zod schema
     * @throws ZodError if user data is invalid
     */
    private validateUser(user: unknown): AuthUser {
        return AuthUserSchema.parse(user);
    }

    /**
     * Store tokens in localStorage and notify listeners
     */
    private storeTokens(tokens: AuthTokens): void {
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        // Store expiration time (expiresIn is in seconds, convert to milliseconds)
        const expiresAt = Date.now() + tokens.expiresIn * 1000;
        localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAt.toString());
        // Dispatch event to notify auth providers that tokens have changed
        window.dispatchEvent(new CustomEvent('auth-tokens-changed'));
    }

    /**
     * Store user in localStorage
     */
    private storeUser(user: AuthUser): void {
        localStorage.setItem(USER_KEY, JSON.stringify(user));
    }

    /**
     * Clear all stored tokens and user data
     */
    private clearTokens(): void {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
        localStorage.removeItem(USER_KEY);
    }
}

export const SelfManagedAuthService = new SelfManagedAuthServiceClass();
